import styled from '@emotion/styled';
import { CustomTheme } from '../../../context/PermissionsContext/ThemeContext/themesTypes';

export const ModalContainer = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  width: 800px;
  top: 30px;
  // height: auto;
  height: 85vh;
  min-height: 700px;
  padding: 0;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  background: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  border-radius: 16px;
  position: relative;
  border: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #d0d2d8)'};
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

export const PageContainer = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  width: 100%;
  height: calc(100% - 50px);
  flex: 1;
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
`;

export const Sidebar = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 198px;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  padding: 24px 12px;
  border-right: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
`;

export const MainContent = styled.div<{ theme?: CustomTheme }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 24px;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  /* Add padding at the bottom to ensure content isn't hidden behind button group */
  padding-bottom: 60px;
`;

export const SidebarItem = styled.div<{
  active?: boolean;
  theme?: CustomTheme;
}>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  background-color: ${({ active, theme }) =>
    active
      ? theme?.primaryLight || 'var(--fi-accent-soft, #FFF0E8)'
      : 'transparent'};
  color: ${({ active, theme }) =>
    active
      ? theme?.primary || 'var(--fi-accent, #FF6A00)'
      : theme?.textLight || 'var(--fi-text-light, #6F878F)'};
  font-weight: ${({ active }) => (active ? 600 : 400)};
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 700;

  &:hover {
    background-color: ${({ active, theme }) =>
      active
        ? theme?.primaryLight || 'var(--fi-accent-soft, #FFF0E8)'
        : theme?.background3 || 'var(--fi-bg3, #f7f7f7)'};
  }
`;

export const SidebarIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
`;

export const Title = styled.div<{ theme?: CustomTheme }>`
  font-size: 16px;
  font-weight: 600;
  padding: 20px 24px;
  border-bottom: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
  width: 100%;
  color: ${({ theme }) => theme?.custom?.text || 'black'};
`;

export const ButtonGroup = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 24px;
  width: 100%;
  border-top: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
  gap: 12px;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
`;

export const Button = styled.button<{
  variant?: 'primary' | 'secondary';
  theme?: CustomTheme;
}>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  min-width: 140px;
  width: 140px;
  justify-content: center;
  transition: all 0.2s;
  height: 50px;
  box-sizing: border-box;

  background-color: ${({ variant, theme }) =>
    variant === 'primary'
      ? theme?.primary || 'var(--fi-button, #FF6A00)'
      : theme?.custom?.background1 || '#ffffff'};
  color: ${({ variant, theme }) =>
    variant === 'primary'
      ? theme?.textCTA || 'var(--fi-button-text, #ffffff)'
      : theme?.primary || 'var(--fi-accent, #FF6A00)'};
  border: ${({ variant, theme }) =>
    variant === 'primary'
      ? 'none'
      : `1px solid ${theme?.primary || 'var(--fi-accent, #FF6A00)'}`};

  span {
    flex: 0 0 auto;
  }

  svg {
    flex: 0 0 auto;
  }

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme?.stroke || 'var(--fi-stroke, #e0e0e0)'
        : theme?.custom?.background1 || '#ffffff'};
    color: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme?.textLight || 'var(--fi-text-light, #999)'
        : theme?.textLight || 'var(--fi-text-light, #cccccc)'};
    border-color: ${({ variant, theme }) =>
      variant === 'primary'
        ? 'transparent'
        : theme?.textLight || 'var(--fi-text-light, #cccccc)'};
    cursor: not-allowed;
  }
`;

export const CloseButton = styled.button<{ theme?: CustomTheme }>`
  position: absolute;
  top: 20px;
  right: 24px;
  background: none;
  border: none;
  color: ${({ theme }) => theme?.textLight || 'var(--fi-text-light, #999)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    color: ${({ theme }) => theme?.text || 'var(--fi-text, #333)'};
  }

  &:disabled {
    color: ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #ccc)'};
    cursor: not-allowed;
  }
`;

export const InputContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #d7d9d6;
  overflow: hidden;
  margin-bottom: 24px;
  max-width: 600px;
`;

export const InputHeader = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #d7d9d6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const InputTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
`;

export const InputBody = styled.div`
  padding: 24px;
`;

export const CustomInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d7d9d6;
  border-radius: 6px;
  font-size: 14px;
  color: #212529;

  &:focus {
    outline: none;
    border-color: var(--fi-accent, #ff6a00);
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

export const CustomSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #d7d9d6;
  border-radius: 6px;
  font-size: 14px;
  color: #212529;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--fi-accent, #ff6a00);
  }
`;

export const ViewContainer = styled.div`
  padding: 125px 100px 40px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const BackButton = styled.div`
  font-family: Space Grotesk;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  width: 500px;
  margin-bottom: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SubmissionButton = styled.div<{ disabled: boolean }>`
  background-color: ${({ disabled }) =>
    disabled ? '#CCCCCC' : 'var(--fi-button, #FF6A00)'};
  width: 500px;
  padding: 20px 0;
  text-align: center;
  color: var(--fi-button-text, #fff);
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 20px;
`;

export const InputTitleContainer = styled.div<{
  align?: string;
  flexDirection?: string;
  theme?: CustomTheme;
}>`
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  display: flex;
  justify-content: ${({ align }) =>
    align === 'center' ? 'center' : 'space-between'};
  flex-direction: ${({ flexDirection }) =>
    flexDirection === 'column' ? 'column' : 'row'};
  align-items: center;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  padding: 0;
`;

export const InputTitleText = styled.div<{ theme?: CustomTheme }>`
  color: ${({ theme }) => theme?.text || 'var(--fi-text, #222428)'};
  font-family: Space Grotesk;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  margin: 16px 0;
`;

export const InputTitleIcon = styled.img`
  width: 20px;
  height: 20px;
`;

export const InputBodyContainer = styled.div<{ theme?: CustomTheme }>`
  background-color: ${({ theme }) => theme?.custom?.background1 || '#ffffff'};
  padding: 16px 0;
`;

export const OptionButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`;

export const OptionButton = styled.button<{
  active?: boolean;
  columns?: number;
  theme?: CustomTheme;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #d7d9d6)'};
  background: ${({ active, theme }) =>
    active
      ? theme?.primaryLight || 'var(--fi-accent-soft, #FFF1E8)'
      : theme?.custom?.background1 || 'white'};
  color: ${({ active, theme }) =>
    active
      ? theme?.primary || 'var(--fi-accent, #FF6A00)'
      : theme?.text || 'var(--fi-text, #495057)'};
  grid-column: ${({ columns }) => (columns === 1 ? 'span 2' : 'auto')};

  &:hover {
    background: ${({ active, theme }) =>
      active
        ? theme?.primaryLight || 'var(--fi-accent-soft, #FFF1E8)'
        : theme?.background3 || 'var(--fi-bg3, #F8F9FA)'};
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

export const FormContainer = styled.div`
  margin-top: 20px;
  width: 500px;
`;
